import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, Facebook, Twitter, Instagram, Youtube, X as CloseIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatDateWithWeekday } from "@/lib/date";
import { TID } from "@/lib/testIds";
import { useDevice } from "@/lib/useDevice";

// Defaults for the explicit header sizing controls (editable from admin).
// Height and logo width are INDEPENDENT: the logo uses height:auto so it can
// stretch/shrink by width without affecting header height.
const HEADER_DEFAULTS = {
  height: { desktop: 76, tablet: 68, mobile: 64 },
  logoWidth: { desktop: 240, tablet: 210, mobile: 180 },
  sidePadding: { desktop: 24, mobile: 18 },
  logoMenuGap: 32,
};

const SOCIAL = [
  { key: "facebook", href: "https://facebook.com/", Icon: Facebook, tid: TID.socialFacebook, label: "Facebook" },
  { key: "twitter", href: "https://x.com/", Icon: Twitter, tid: TID.socialTwitter, label: "X" },
  { key: "instagram", href: "https://instagram.com/", Icon: Instagram, tid: TID.socialInstagram, label: "Instagram" },
  { key: "youtube", href: "https://youtube.com/", Icon: Youtube, tid: TID.socialYoutube, label: "YouTube" },
];

export default function Header() {
  const [categories, setCategories] = useState([]);
  // Init from cache so the logo appears instantly on subsequent loads (no flash).
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("sp_site_settings_cache") : null;
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [settingsLoaded, setSettingsLoaded] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("sp_site_settings_cache") : null;
      return !!raw;
    } catch { return false; }
  });
  const [now, setNow] = useState(new Date());
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.categories().then(setCategories).catch(() => setCategories([]));
    api.siteSettings()
      .then((s) => {
        const next = s || {};
        setSiteSettings(next);
        try { localStorage.setItem("sp_site_settings_cache", JSON.stringify(next)); } catch { /* ignore */ }
      })
      .catch(() => { /* keep cached */ })
      .finally(() => setSettingsLoaded(true));
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const primaryCats = categories.slice(0, 8);
  const secondaryCats = categories.slice(8);

  // Fondo de la fila del logo (editable desde admin). Default: azul petróleo.
  const brandBg = siteSettings.header_brand_bg_color || "#073B4C";

  // ------------------------------------------------------------------
  // Header sizing: three independent responsive variables (height, logo
  // width, side padding) driven by admin settings. `useDevice` gives us
  // the actual breakpoint (mobile <768, tablet <1024, desktop ≥1024).
  // ------------------------------------------------------------------
  const device = useDevice(); // "mobile" | "tablet" | "desktop"
  const pickH = () => {
    if (device === "mobile") return siteSettings.header_height_mobile || HEADER_DEFAULTS.height.mobile;
    if (device === "tablet") return siteSettings.header_height_tablet || HEADER_DEFAULTS.height.tablet;
    return siteSettings.header_height_desktop || HEADER_DEFAULTS.height.desktop;
  };
  const pickLogoW = () => {
    if (device === "mobile") return siteSettings.logo_width_mobile || HEADER_DEFAULTS.logoWidth.mobile;
    if (device === "tablet") return siteSettings.logo_width_tablet || HEADER_DEFAULTS.logoWidth.tablet;
    return siteSettings.logo_width_desktop || HEADER_DEFAULTS.logoWidth.desktop;
  };
  const pickSidePad = () => {
    if (device === "desktop") return siteSettings.header_side_padding_desktop || HEADER_DEFAULTS.sidePadding.desktop;
    return siteSettings.header_side_padding_mobile || HEADER_DEFAULTS.sidePadding.mobile;
  };
  const headerHeight = pickH();
  const logoWidth = pickLogoW();
  const sidePad = pickSidePad();
  const logoGap = siteSettings.header_logo_menu_gap != null ? siteSettings.header_logo_menu_gap : HEADER_DEFAULTS.logoMenuGap;

  // Sencillo cálculo de luminancia para decidir color de texto contrastado.
  const isDarkBrand = (() => {
    const hex = brandBg.replace("#", "");
    if (hex.length !== 6) return true;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.55;
  })();

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setMobileOpen(false);
    navigate(`/buscar?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header data-testid={TID.header} className="sticky top-0 z-40 bg-white border-b border-softblue">
      {/* Top strip: date + social */}
      <div className="bg-petroleum text-white">
        <div className="container-wide flex items-center justify-between py-1.5 text-[11px] uppercase tracking-[0.14em] font-medium">
          <span data-testid={TID.headerDate} className="truncate">
            {formatDateWithWeekday(now)} · Río Grande, Tierra del Fuego
          </span>
          <div className="hidden sm:flex items-center gap-3">
            {SOCIAL.map(({ key, href, Icon, tid, label }) => (
              <a
                key={key}
                href={href}
                data-testid={tid}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-watergreen transition-colors"
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Brand row */}
      <div style={{ backgroundColor: brandBg }} data-testid="header-brand-row">
        <div
          className="container-wide flex items-center justify-between"
          style={{
            minHeight: `${headerHeight}px`,
            paddingLeft: `${sidePad}px`,
            paddingRight: `${sidePad}px`,
            gap: `${logoGap}px`,
          }}
        >
          <Link
            to="/"
            data-testid={TID.headerLogo}
            className="flex items-center group flex-shrink-0"
            style={{ width: `${logoWidth}px`, maxWidth: `${logoWidth}px` }}
          >
            {!settingsLoaded ? (
              // Reserva de espacio mientras cargan settings (primera visita sin caché)
              // para evitar mostrar el texto "SurPrimicia" antes de conocer logo_url.
              <div aria-hidden="true" style={{ width: `${logoWidth}px`, height: `${Math.round(logoWidth * 0.36)}px` }} />
            ) : siteSettings.logo_url ? (
              // Logo image: width fija, height:auto — desacoplado del alto del header.
              <img
                src={siteSettings.logo_url}
                alt={siteSettings.site_name || "Sur Primicia"}
                style={{ width: `${logoWidth}px`, height: "auto", maxWidth: "100%" }}
                className="block object-contain"
              />
            ) : (
              // Fallback tipográfico cuando aún no hay logo cargado.
              <div className="flex flex-col leading-none w-full">
                <span
                  className={`font-serif font-black tracking-tight ${isDarkBrand ? "text-white" : "text-nightblue"}`}
                  style={{ fontSize: `${Math.round(logoWidth * 0.16)}px`, lineHeight: 1 }}
                >
                  Sur<span className="text-turquoise">Primicia</span>
                </span>
                <span
                  className={`hidden sm:block uppercase tracking-[0.28em] mt-1 ${isDarkBrand ? "text-white/70" : "text-textgray"}`}
                  style={{ fontSize: `${Math.max(9, Math.round(logoWidth * 0.045))}px` }}
                >
                  {siteSettings.tagline || "El pulso informativo del sur argentino"}
                </span>
              </div>
            )}
          </Link>

          {/* Desktop search */}
          <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 max-w-md w-full">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
              <Input
                data-testid={TID.searchInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar noticias, autores, temas…"
                className="pl-9 h-10 bg-white border-softblue focus-visible:ring-turquoise"
              />
            </div>
            <Button
              type="submit"
              data-testid={TID.searchSubmit}
              className="h-10 bg-nightblue hover:bg-blueblack text-white rounded-none px-5 font-semibold uppercase text-xs tracking-widest"
            >
              Buscar
            </Button>
          </form>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid={TID.mobileMenuButton}
                  aria-label="Abrir menú"
                  className={`inline-flex items-center justify-center h-10 w-10 border ${
                    isDarkBrand ? "border-white/40 text-white" : "border-softblue text-nightblue"
                  }`}
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" data-testid={TID.mobileMenu} className="w-[85vw] sm:w-96 bg-white border-r border-softblue overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="font-serif text-2xl text-nightblue">Menú</SheetTitle>
                </SheetHeader>
                <form onSubmit={submitSearch} className="mt-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
                    <Input
                      data-testid={TID.searchInputMobile}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Buscar…"
                      className="pl-9 h-11 bg-lightgray border-softblue"
                    />
                  </div>
                </form>
                <nav className="mt-6 border-t border-softblue">
                  {categories.map((c) => (
                    <NavLink
                      key={c.id}
                      to={`/categoria/${c.slug}`}
                      data-testid={TID.mobileNavCategoryLink(c.slug)}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `block py-3 text-sm font-medium border-b border-softblue transition-colors ${
                          isActive ? "text-turquoise" : "text-nightblue hover:text-turquoise"
                        }`
                      }
                    >
                      {c.name}
                    </NavLink>
                  ))}
                  <Link
                    to="/quienes-somos"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-medium border-b border-softblue text-textgray hover:text-turquoise"
                  >
                    Quiénes somos
                  </Link>
                  <Link
                    to="/contacto"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-medium text-textgray hover:text-turquoise"
                  >
                    Contacto
                  </Link>
                </nav>
                <div className="mt-6 flex items-center gap-4 text-nightblue">
                  {SOCIAL.map(({ key, href, Icon, label }) => (
                    <a key={key} href={href} aria-label={label} target="_blank" rel="noopener noreferrer" className="hover:text-turquoise transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </a>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Category nav (desktop) */}
      <nav
        className="hidden md:block"
        style={{
          backgroundColor: siteSettings.category_bar_bg_color || "#071E33",
          color: siteSettings.category_bar_text_color || "#FFFFFF",
        }}
        data-testid="category-bar"
      >
        <div className="container-wide flex items-center gap-1">
          {primaryCats.map((c) => (
            <NavLink
              key={c.id}
              to={`/categoria/${c.slug}`}
              data-testid={TID.navCategoryLink(c.slug)}
              className={({ isActive }) =>
                `whitespace-nowrap px-3.5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] border-b-2 transition-colors ${
                  isActive
                    ? "border-watergreen text-watergreen"
                    : "border-transparent hover:border-watergreen hover:text-watergreen"
                }`
              }
            >
              {c.name}
            </NavLink>
          ))}
          {secondaryCats.length > 0 && (
            <div className="relative group ml-1">
              <button className="whitespace-nowrap px-3.5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] hover:text-watergreen">
                Más categorías ▾
              </button>
              <div className="absolute left-0 top-full min-w-[220px] bg-white text-nightblue border border-softblue border-t-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
                {secondaryCats.map((c) => (
                  <NavLink
                    key={c.id}
                    to={`/categoria/${c.slug}`}
                    data-testid={TID.navCategoryLink(c.slug)}
                    className="block px-4 py-2.5 text-xs uppercase tracking-[0.12em] font-semibold border-b border-softblue last:border-b-0 hover:bg-lightgray hover:text-turquoise"
                  >
                    {c.name}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}

export { CloseIcon };
